import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, getApiList } from '../api';

function AuditLog() {
  const navigate = useNavigate();

  const [logs, setLogs] = useState([]);
  const [sourceMode, setSourceMode] = useState('audit');
  const [transactions, setTransactions] = useState([]);
  const [stores, setStores] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchJsonSafe = async (path) => {
    try {
      const response = await apiFetch(path);

      if (!response || !response.ok) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error(`Error loading ${path}:`, error);
      return null;
    }
  };

  const normalizeJournalLog = (log) => ({
    id: log.id,
    action: log.type,
    type: log.type,
    entity_type: 'journal',
    entity_id: log.id,
    amount: log.amount,
    description: log.description,
    store_id: log.store_id,
    wallet_id: log.wallet_id,
    user_id: log.user_id,
    user_name: null,
    user_role: null,
    page: '/journal',
    old_value: null,
    new_value: {
      type: log.type,
      amount: log.amount,
      store_id: log.store_id,
      wallet_id: log.wallet_id,
      user_id: log.user_id,
      description: log.description
    },
    created_at: log.created_at,
    __source: 'journal'
  });

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError('');

      const [
        auditData,
        transactionsData,
        storesData,
        walletsData,
        usersData
      ] = await Promise.all([
        fetchJsonSafe('/audit-logs'),
        fetchJsonSafe('/transactions'),
        fetchJsonSafe('/stores'),
        fetchJsonSafe('/wallets'),
        fetchJsonSafe('/users')
      ]);

      const auditRows = Array.isArray(auditData?.logs)
        ? auditData.logs
        : Array.isArray(auditData)
          ? auditData
          : [];

      setLogs(auditRows.map((log) => ({
        ...log,
        type: log.action || log.type,
        __source: 'audit'
      })));
      setSourceMode('audit');

      setTransactions(getApiList(transactionsData, 'transactions'));
      setStores(getApiList(storesData, 'stores'));
      setWallets(getApiList(walletsData, 'wallets'));
      setUsers(getApiList(usersData, 'users'));
    } catch (err) {
      console.error(err);
      setError('حدث خطأ أثناء جلب سجل النشاطات');
    } finally {
      setLoading(false);
    }
  };

  const normalizeArabic = (value) => {
    return String(value || '')
      .toLowerCase()
      .replace(/[أإآا]/g, 'ا')
      .replace(/[ى]/g, 'ي')
      .replace(/[ؤ]/g, 'و')
      .replace(/[ئ]/g, 'ي')
      .replace(/[ة]/g, 'ه')
      .replace(/[٠-٩]/g, (digit) => '٠١٢٣٤٥٦٧٨٩'.indexOf(digit))
      .replace(/\s+/g, ' ')
      .trim();
  };

  const safeJsonParse = (value) => {
    if (!value) return null;

    if (typeof value === 'object') {
      return value;
    }

    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      recharge: '🔄',
      collection: '💵',
      transfer: '↔️',
      transfer_pending: '⏳',
      transfer_confirmed: '✅',
      expense: '💸',
      company_settlement: '🏢',
      create: '➕',
      update: '✏️',
      delete: '🗑️',
      restore: '♻️',
      login: '🔐',
      backup: '💾',
      user_transfer: '↔️'
    };

    return icons[type] || '📝';
  };

  const getTypeLabel = (type) => {
    const labels = {
      recharge: 'شحن',
      collection: 'قبض',
      transfer: 'تحويل',
      transfer_pending: 'تحويل معلق',
      transfer_confirmed: 'تأكيد تحويل',
      expense: 'مصروف',
      company_settlement: 'تسديد شركة',
      create: 'إنشاء',
      update: 'تعديل',
      delete: 'حذف',
      restore: 'استرجاع',
      login: 'تسجيل دخول',
      backup: 'نسخة احتياطية',
      user_transfer: 'تحويل مستخدم'
    };

    return labels[type] || type || '-';
  };

  const getEntityLabel = (type) => {
    const labels = {
      store: 'محل',
      wallet: 'محفظة',
      user: 'مستخدم',
      user_balance: 'رصيد مستخدم',
      user_transfer: 'تحويل مستخدم',
      transaction: 'عملية',
      journal: 'دفتر اليومية',
      settings: 'إعدادات',
      system: 'النظام'
    };

    return labels[type] || type || '-';
  };

  const getTypeClass = (type) => {
    const classes = {
      recharge: 'bg-blue-100 text-blue-800 border-blue-200',
      collection: 'bg-green-100 text-green-800 border-green-200',
      transfer: 'bg-purple-100 text-purple-800 border-purple-200',
      transfer_pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      transfer_confirmed: 'bg-green-100 text-green-800 border-green-200',
      expense: 'bg-red-100 text-red-800 border-red-200',
      company_settlement: 'bg-orange-100 text-orange-800 border-orange-200',
      create: 'bg-green-100 text-green-800 border-green-200',
      update: 'bg-blue-100 text-blue-800 border-blue-200',
      delete: 'bg-red-100 text-red-800 border-red-200',
      restore: 'bg-emerald-100 text-emerald-800 border-emerald-200'
    };

    return classes[type] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const formatNumber = (value) => {
    const number = Number(value || 0);

    if (!Number.isFinite(number)) {
      return '0';
    }

    return number.toLocaleString('en-US', {
      maximumFractionDigits: 2
    });
  };

  const formatDateTime = (value) => {
    if (!value) return '-';

    try {
      return new Date(value).toLocaleString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const formatTime = (value) => {
    if (!value) return '-';

    try {
      return new Date(value).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const isToday = (value) => {
    if (!value) return false;

    const logDate = new Date(value);
    const today = new Date();

    return (
      logDate.getFullYear() === today.getFullYear() &&
      logDate.getMonth() === today.getMonth() &&
      logDate.getDate() === today.getDate()
    );
  };

  const getStoreName = (storeId) => {
    if (!storeId) return '-';
    return stores.find(store => store.id === storeId)?.name || String(storeId).slice(0, 8);
  };

  const getWalletName = (walletId) => {
    if (!walletId) return '-';
    return wallets.find(wallet => wallet.id === walletId)?.name || String(walletId).slice(0, 8);
  };

  const getUserName = (userId, fallbackName = null) => {
    if (fallbackName) return fallbackName;
    if (!userId) return '-';
    return users.find(user => user.id === userId)?.name || String(userId).slice(0, 8);
  };

  const extractAmount = (log) => {
    if (log.amount) return Number(log.amount || 0);

    const oldValue = safeJsonParse(log.old_value);
    const newValue = safeJsonParse(log.new_value);

    return Number(
      newValue?.amount ||
      oldValue?.amount ||
      newValue?.balance ||
      oldValue?.balance ||
      0
    );
  };

  const extractStoreId = (log) => {
    const oldValue = safeJsonParse(log.old_value);
    const newValue = safeJsonParse(log.new_value);

    return log.store_id || newValue?.store_id || oldValue?.store_id || (log.entity_type === 'store' ? log.entity_id : null);
  };

  const extractWalletId = (log) => {
    const oldValue = safeJsonParse(log.old_value);
    const newValue = safeJsonParse(log.new_value);

    return log.wallet_id || newValue?.wallet_id || oldValue?.wallet_id || (log.entity_type === 'wallet' ? log.entity_id : null);
  };

  const findRelatedTransaction = (log) => {
    if (!log) return null;

    return transactions.find((tx) => {
      if (tx.id === log.transaction_id || tx.id === log.entity_id) return true;

      const sameType = tx.type === log.type || tx.type === log.action;
      const sameAmount = Number(tx.amount || 0) === Number(extractAmount(log) || 0);
      const storeId = extractStoreId(log);
      const walletId = extractWalletId(log);
      const sameStore = !storeId || tx.store_id === storeId;
      const sameWallet = !walletId || tx.wallet_id === walletId;

      return sameType && sameAmount && sameStore && sameWallet;
    }) || null;
  };

  const typeOptions = useMemo(() => {
    const values = Array.from(new Set(logs.map(log => log.action || log.type).filter(Boolean)));
    const preferred = [
      'all',
      'create',
      'update',
      'delete',
      'restore',
      'recharge',
      'collection',
      'transfer_pending',
      'transfer_confirmed',
      'expense',
      'company_settlement'
    ];

    const all = ['all', ...values];
    return preferred.filter(item => all.includes(item)).concat(
      all.filter(item => !preferred.includes(item))
    );
  }, [logs]);

  const entityOptions = useMemo(() => {
    const values = Array.from(new Set(logs.map(log => log.entity_type).filter(Boolean)));
    return ['all', ...values];
  }, [logs]);

  const filteredLogs = useMemo(() => {
    const query = normalizeArabic(searchText);

    return logs
      .filter((log) => {
        const type = log.action || log.type;
        if (filterType === 'all') return true;
        return type === filterType;
      })
      .filter((log) => {
        if (entityFilter === 'all') return true;
        return log.entity_type === entityFilter;
      })
      .map((log) => {
        const type = log.action || log.type;
        const transaction = findRelatedTransaction(log);
        const oldValue = safeJsonParse(log.old_value);
        const newValue = safeJsonParse(log.new_value);
        const storeId = extractStoreId(log);
        const walletId = extractWalletId(log);
        const amount = extractAmount(log);

        const fields = [
          log.id,
          type,
          getTypeLabel(type),
          log.entity_type,
          getEntityLabel(log.entity_type),
          log.entity_id,
          amount,
          log.description,
          log.page,
          storeId,
          getStoreName(storeId),
          walletId,
          getWalletName(walletId),
          log.user_id,
          getUserName(log.user_id, log.user_name),
          transaction?.id,
          transaction?.status,
          transaction?.description,
          JSON.stringify(oldValue || ''),
          JSON.stringify(newValue || ''),
          formatDateTime(log.created_at)
        ];

        const text = normalizeArabic(fields.filter(Boolean).join(' | '));

        let score = 10;

        if (query) {
          score = 0;
          if (text.startsWith(query)) score = 100;
          else if (text.includes(query)) score = 60;
        }

        return {
          ...log,
          type,
          transaction,
          oldValue,
          newValue,
          storeId,
          walletId,
          amount,
          score
        };
      })
      .filter((log) => log.score > 0)
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });
  }, [logs, transactions, stores, wallets, users, filterType, entityFilter, searchText]);

  const totalAmount = filteredLogs.reduce((sum, log) => {
    return sum + Number(log.amount || 0);
  }, 0);

  const todayLogsCount = logs.filter((log) => isToday(log.created_at)).length;
  const lastLogTime = logs.length > 0
    ? [...logs].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))[0]?.created_at
    : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50" dir="rtl">
        <div className="bg-white rounded-3xl shadow p-8 text-xl font-bold text-gray-700">
          جاري تحميل سجل النشاطات...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8" dir="rtl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-br from-gray-950 via-orange-900 to-black text-white rounded-3xl p-5 md:p-8 shadow-lg mb-6">
          <button
            onClick={() => navigate('/')}
            className="bg-white/10 hover:bg-white/20 text-white font-bold px-4 py-2 rounded-xl mb-5"
          >
            ← رجوع للرئيسية
          </button>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <div>
              <h1 className="text-3xl md:text-5xl font-bold">
                📋 سجل النشاطات
              </h1>

              <p className="text-orange-100 mt-3 text-sm md:text-base">
                تتبع من قام بالعملية، نوع العملية، الصفحة، والقيم قبل وبعد التعديل
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
              <MiniStat label="السجلات" value={logs.length} />
              <MiniStat label="نتائج الفلتر" value={filteredLogs.length} />
              <MiniStat label="اليوم" value={todayLogsCount} />
              <MiniStat label="آخر تحديث" value={formatTime(lastLogTime)} />
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl px-4 py-3 mb-6">
            ⚠️ {error}
          </div>
        )}

        <div className={`rounded-3xl shadow p-5 md:p-6 mb-6 border ${
          'bg-green-50 border-green-100 text-green-800'
        }`}>
          <h2 className="text-2xl font-bold mb-3">
            ✅ سجل النشاطات الحقيقي مفعّل
          </h2>

          <p className="leading-7">
            الصفحة تعرض بيانات audit_logs الحقيقية من السيرفر بدون وضع احتياطي.
          </p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-3xl shadow p-5 md:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-5">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                🔎 البحث الذكي في سجل النشاطات
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                ابحث بالعملية، المستخدم، الصفحة، الجهة، القيم قبل/بعد، المحل، المحفظة، أو التاريخ
              </p>
            </div>

            <button
              onClick={fetchLogs}
              disabled={loading}
              className="bg-gray-800 hover:bg-black disabled:opacity-50 text-white font-bold py-3 px-5 rounded-xl"
            >
              🔄 تحديث
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 mb-4">
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="اكتب أي شيء للبحث..."
              className="lg:col-span-3 w-full border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4 bg-gray-50"
            />

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4 bg-gray-50"
            >
              {typeOptions.map(type => (
                <option key={type} value={type}>
                  {type === 'all' ? 'كل العمليات' : getTypeLabel(type)}
                </option>
              ))}
            </select>

            <select
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.target.value)}
              className="border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4 bg-gray-50"
            >
              {entityOptions.map(entity => (
                <option key={entity} value={entity}>
                  {entity === 'all' ? 'كل الجهات' : getEntityLabel(entity)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-2 flex-wrap">
            {typeOptions.map((type) => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`py-2 px-4 rounded-xl font-bold transition border ${
                  filterType === type
                    ? 'bg-orange-600 text-white border-orange-600'
                    : 'bg-white text-gray-800 hover:bg-gray-50 border-gray-200'
                }`}
              >
                {type === 'all'
                  ? '📊 الكل'
                  : `${getTypeIcon(type)} ${getTypeLabel(type)}`}
              </button>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <SummaryCard
            title="إجمالي السجلات"
            value={logs.length}
            note={'من audit_logs'}
            color="blue"
          />

          <SummaryCard
            title="مبالغ الفلتر"
            value={formatNumber(totalAmount)}
            note="حسب البحث والفلترة الحالية"
            color="green"
          />

          <SummaryCard
            title="عمليات اليوم"
            value={todayLogsCount}
            note="سجلات بتاريخ اليوم"
            color="purple"
          />

          <SummaryCard
            title="آخر تحديث"
            value={formatTime(lastLogTime)}
            note={formatDateTime(lastLogTime)}
            color="orange"
          />
        </div>

        {/* Logs */}
        <div className="bg-white rounded-3xl shadow p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                📒 السجلات
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                عدد النتائج: {filteredLogs.length}
              </p>
            </div>
          </div>

          {filteredLogs.length === 0 ? (
            <div className="text-center text-gray-500 py-10 bg-gray-50 rounded-2xl">
              لا توجد سجلات مطابقة
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:hidden gap-4">
                {filteredLogs.map((log) => (
                  <LogCard
                    key={log.id}
                    log={log}
                    getTypeIcon={getTypeIcon}
                    getTypeLabel={getTypeLabel}
                    getTypeClass={getTypeClass}
                    getEntityLabel={getEntityLabel}
                    formatNumber={formatNumber}
                    formatDateTime={formatDateTime}
                    getStoreName={getStoreName}
                    getWalletName={getWalletName}
                    getUserName={getUserName}
                    onDetails={() => setSelectedLog(log)}
                  />
                ))}
              </div>

              <div className="hidden xl:block overflow-x-auto">
                <table className="w-full text-sm text-right">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-5 py-3 text-right text-gray-600">العملية</th>
                      <th className="px-5 py-3 text-right text-gray-600">الجهة</th>
                      <th className="px-5 py-3 text-right text-gray-600">المبلغ</th>
                      <th className="px-5 py-3 text-right text-gray-600">المستخدم</th>
                      <th className="px-5 py-3 text-right text-gray-600">المحل</th>
                      <th className="px-5 py-3 text-right text-gray-600">المحفظة</th>
                      <th className="px-5 py-3 text-right text-gray-600">الصفحة</th>
                      <th className="px-5 py-3 text-right text-gray-600">التاريخ والوقت</th>
                      <th className="px-5 py-3 text-right text-gray-600">إجراء</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredLogs.map((log) => (
                      <tr key={log.id} className="border-b hover:bg-gray-50">
                        <td className="px-5 py-4">
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border ${getTypeClass(log.type)}`}>
                            <span>{getTypeIcon(log.type)}</span>
                            <span>{getTypeLabel(log.type)}</span>
                          </span>
                        </td>

                        <td className="px-5 py-4 text-gray-700">
                          {getEntityLabel(log.entity_type)}
                        </td>

                        <td className="px-5 py-4 font-bold text-green-700">
                          {formatNumber(log.amount)}
                        </td>

                        <td className="px-5 py-4 text-gray-600">
                          {getUserName(log.user_id, log.user_name)}
                        </td>

                        <td className="px-5 py-4 text-gray-600">
                          {getStoreName(log.storeId)}
                        </td>

                        <td className="px-5 py-4 text-gray-600">
                          {getWalletName(log.walletId)}
                        </td>

                        <td className="px-5 py-4 text-gray-500 max-w-[220px] truncate">
                          {log.page || '-'}
                        </td>

                        <td className="px-5 py-4 text-xs text-gray-500">
                          {formatDateTime(log.created_at)}
                        </td>

                        <td className="px-5 py-4">
                          <button
                            onClick={() => setSelectedLog(log)}
                            className="bg-gray-800 hover:bg-black text-white px-3 py-2 rounded-xl text-sm font-bold"
                          >
                            تفاصيل
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {selectedLog && (
        <LogDetailsModal
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
          getTypeIcon={getTypeIcon}
          getTypeLabel={getTypeLabel}
          getTypeClass={getTypeClass}
          getEntityLabel={getEntityLabel}
          formatNumber={formatNumber}
          formatDateTime={formatDateTime}
          getStoreName={getStoreName}
          getWalletName={getWalletName}
          getUserName={getUserName}
          safeJsonParse={safeJsonParse}
        />
      )}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="bg-white/10 border border-white/10 rounded-2xl p-3 min-w-[95px]">
      <p className="text-orange-100 text-xs">{label}</p>
      <p className="text-white font-bold text-xl mt-1">{value}</p>
    </div>
  );
}

function SummaryCard({ title, value, note, color }) {
  const colors = {
    blue: 'border-blue-500 text-blue-700 bg-blue-50',
    green: 'border-green-500 text-green-700 bg-green-50',
    purple: 'border-purple-500 text-purple-700 bg-purple-50',
    orange: 'border-orange-500 text-orange-700 bg-orange-50'
  };

  return (
    <div className={`bg-white rounded-2xl shadow p-5 border-r-4 ${colors[color] || colors.blue}`}>
      <p className="text-gray-600 text-sm">{title}</p>
      <p className="text-2xl md:text-3xl font-bold mt-2">{value}</p>
      <p className="text-gray-500 text-xs mt-2">{note}</p>
    </div>
  );
}

function LogCard({
  log,
  getTypeIcon,
  getTypeLabel,
  getTypeClass,
  getEntityLabel,
  formatNumber,
  formatDateTime,
  getStoreName,
  getWalletName,
  getUserName,
  onDetails
}) {
  return (
    <div className="bg-gray-50 border border-gray-100 rounded-3xl p-4">
      <div className="flex items-start justify-between gap-3 mb-4">
        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border ${getTypeClass(log.type)}`}>
          <span>{getTypeIcon(log.type)}</span>
          <span>{getTypeLabel(log.type)}</span>
        </span>

        <span className="bg-white border border-gray-100 px-3 py-1 rounded-full text-xs text-gray-500">
          #{String(log.id).slice(0, 8)}
        </span>
      </div>

      <p className="text-3xl font-bold text-green-700 mb-3">
        {formatNumber(log.amount)}
      </p>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <Info label="المستخدم" value={getUserName(log.user_id, log.user_name)} />
        <Info label="الجهة" value={getEntityLabel(log.entity_type)} />
        <Info label="المحل" value={getStoreName(log.storeId)} />
        <Info label="المحفظة" value={getWalletName(log.walletId)} />
        <div className="col-span-2">
          <Info label="التاريخ" value={formatDateTime(log.created_at)} />
        </div>
      </div>

      <button
        onClick={onDetails}
        className="w-full bg-gray-800 hover:bg-black text-white font-bold py-3 rounded-2xl"
      >
        تفاصيل السجل
      </button>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="bg-white rounded-2xl p-3 border border-gray-100">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="font-bold text-gray-900 truncate">{value}</p>
    </div>
  );
}

function LogDetailsModal({
  log,
  onClose,
  getTypeIcon,
  getTypeLabel,
  getTypeClass,
  getEntityLabel,
  formatNumber,
  formatDateTime,
  getStoreName,
  getWalletName,
  getUserName
}) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4" dir="rtl">
      <div className="bg-white rounded-t-3xl md:rounded-3xl shadow-2xl w-full md:max-w-5xl max-h-[92vh] overflow-y-auto">
        <div className="p-5 md:p-6 border-b flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              🧾 تفاصيل السجل
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              #{String(log.id).slice(0, 12)}
            </p>
          </div>

          <button
            onClick={onClose}
            className="bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl px-4 py-2 font-bold"
          >
            إغلاق
          </button>
        </div>

        <div className="p-5 md:p-6">
          <div className="mb-5">
            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border ${getTypeClass(log.type)}`}>
              <span>{getTypeIcon(log.type)}</span>
              <span>{getTypeLabel(log.type)}</span>
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Detail label="رقم السجل" value={log.id} />
            <Detail label="نوع العملية" value={getTypeLabel(log.type)} />
            <Detail label="الجهة" value={getEntityLabel(log.entity_type)} />
            <Detail label="رقم الجهة" value={log.entity_id || '-'} />
            <Detail label="المبلغ" value={formatNumber(log.amount)} />
            <Detail label="التاريخ والوقت" value={formatDateTime(log.created_at)} />
            <Detail label="من قام بالعملية" value={getUserName(log.user_id, log.user_name)} />
            <Detail label="صلاحية المستخدم" value={log.user_role || '-'} />
            <Detail label="المحل" value={getStoreName(log.storeId)} />
            <Detail label="المحفظة" value={getWalletName(log.walletId)} />
            <Detail label="الصفحة / المسار" value={log.page || '-'} />
            <Detail label="مصدر السجل" value="audit_logs" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-5">
            <JsonBox title="القيم قبل التعديل" value={log.oldValue || log.old_value} />
            <JsonBox title="القيم بعد التعديل" value={log.newValue || log.new_value} />
          </div>

          {log.transaction && (
            <div className="mt-5 bg-blue-50 border border-blue-100 rounded-2xl p-4">
              <p className="text-blue-800 font-bold mb-2">عملية مالية مرتبطة</p>
              <pre className="text-xs text-gray-700 whitespace-pre-wrap overflow-x-auto">
                {JSON.stringify(log.transaction, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function JsonBox({ title, value }) {
  let displayValue = value || null;

  if (typeof displayValue === 'string') {
    try {
      displayValue = JSON.parse(displayValue);
    } catch {
      // keep string
    }
  }

  return (
    <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4">
      <p className="text-gray-800 font-bold mb-3">{title}</p>
      {displayValue ? (
        <pre className="text-xs text-gray-700 whitespace-pre-wrap overflow-x-auto">
          {typeof displayValue === 'string'
            ? displayValue
            : JSON.stringify(displayValue, null, 2)}
        </pre>
      ) : (
        <p className="text-gray-500 text-sm">لا توجد بيانات</p>
      )}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4">
      <p className="text-gray-500 text-sm mb-1">{label}</p>
      <p className="text-gray-900 font-bold break-words">{value}</p>
    </div>
  );
}

export default AuditLog;
